import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEmployee, usePenalties, useTimesheets } from '@/api';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs } from '@/components/ui';
import { LoadingSpinner } from '@/components/common';
import { ArrowLeft, Clock, AlertTriangle } from 'lucide-react';

const unwrapRows = (response: any) => {
  const body = response?.data;
  const data = body?.data ?? body ?? [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('ru-RU');
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
};

const timesheetStatus: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'warning' | 'success' }> = {
  present: { label: 'Присутствовал', variant: 'success' },
  late: { label: 'Опоздал', variant: 'warning' },
  early_leave: { label: 'Ранний уход', variant: 'warning' },
  absent: { label: 'Отсутствовал', variant: 'destructive' },
  no_show: { label: 'Не вышел', variant: 'destructive' },
  no_checkout: { label: 'Нет ухода', variant: 'warning' },
  pending: { label: 'Ожидается', variant: 'secondary' },
  day_off: { label: 'Выходной', variant: 'secondary' },
  weekend: { label: 'Выходной', variant: 'secondary' },
  holiday: { label: 'Праздник', variant: 'secondary' },
  leave: { label: 'Отпуск', variant: 'secondary' },
  unpaid_leave: { label: 'Отпуск без оплаты', variant: 'secondary' },
};

const penaltyStatus: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'warning' | 'success' }> = {
  pending_approval: { label: 'Ожидает подтверждения', variant: 'warning' },
  auto_generated: { label: 'Авто-создан', variant: 'warning' },
  approved: { label: 'Подтверждён', variant: 'success' },
  overridden: { label: 'Изменён', variant: 'default' },
  cancelled: { label: 'Возвращён', variant: 'secondary' },
  rejected: { label: 'Отклонён', variant: 'destructive' },
};

const penaltyType: Record<string, string> = {
  late: 'Опоздание',
  early_leave: 'Ранний уход',
  absent: 'Прогул',
  no_show: 'Не явился',
  no_checkout: 'Не отметил уход',
  no_checkin: 'Не отметил приход',
  manual: 'Ручной штраф',
};

export default function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: employeeData, isLoading } = useEmployee(Number(id));

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const employee = employeeData?.data?.data ?? employeeData?.data;

  if (!employee) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold">Сотрудник не найден</h2>
        <Button onClick={() => navigate('/employees')} className="mt-4">
          Вернуться к сотрудникам
        </Button>
      </div>
    );
  }

  const tabs = [
    {
      label: 'Профиль',
      content: <ProfileTab employee={employee} />,
    },
    {
      label: 'Посещаемость',
      content: <AttendanceTab employeeId={employee.id} />,
    },
    {
      label: 'Штрафы',
      content: <PenaltiesTab employeeId={employee.id} />,
    },
    {
      label: 'Привязки к устройствам',
      content: <DeviceBindingsTab employee={employee} />,
    },
  ];

  return (
    <div className="space-y-6 page-shell">
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <Button variant="ghost" onClick={() => navigate('/employees')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{employee.full_name}</h1>
          <p className="text-gray-500">ID сотрудника: {employee.employee_id}</p>
        </div>
        <Badge variant={employee.is_active ? 'success' : 'secondary'}>
          {employee.is_active ? 'Активен' : 'Неактивен'}
        </Badge>
      </div>

      <Tabs tabs={tabs} />
    </div>
  );
}

function ProfileTab({ employee }: { employee: any }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Личная информация</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="text-sm text-gray-500">Полное имя</label>
            <p className="font-medium">{employee.full_name}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Email</label>
            <p className="font-medium">{employee.email || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Телефон</label>
            <p className="font-medium">{employee.phone || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Telegram ID</label>
            <p className="font-medium">{employee.telegram_id || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Филиал</label>
            <p className="font-medium">{employee.branch?.name || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Отдел</label>
            <p className="font-medium">{employee.department?.name || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Смена по умолчанию</label>
            <p className="font-medium">{employee.default_shift?.name || employee.defaultShift?.name || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Дата приема</label>
            <p className="font-medium">
              {formatDate(employee.hire_date)}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Штрафы к удержанию за месяц</label>
            <p className="font-medium">{Number(employee.stats?.penalties_total || 0).toLocaleString('ru-RU')} сум</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Возвращённые штрафы за месяц</label>
            <p className="font-medium">{employee.stats?.returned_penalties_count ?? 0}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AttendanceTab({ employeeId }: { employeeId: number }) {
  const { data, isLoading } = useTimesheets({ employee_id: employeeId, per_page: 10 });
  const rows = unwrapRows(data);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Последняя посещаемость
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSpinner />
        ) : rows.length === 0 ? (
          <p className="text-gray-500">Записи посещаемости не найдены.</p>
        ) : (
          <div className="space-y-3">
            {rows.map((row: any) => {
              const status = timesheetStatus[row.status] || { label: row.status, variant: 'secondary' as const };
              return (
                <div key={row.id} className="flex flex-col gap-2 rounded-lg border border-slate-200 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium">{formatDate(row.work_date)}</p>
                    <p className="text-sm text-gray-500">
                      Вход: {formatDateTime(row.first_in_at)} · Выход: {formatDateTime(row.last_out_at)}
                    </p>
                    {(row.late_minutes > 0 || row.early_leave_minutes > 0) && (
                      <p className="text-xs text-amber-700">
                        Опоздание: {row.late_minutes || 0} мин · Ранний уход: {row.early_leave_minutes || 0} мин
                      </p>
                    )}
                  </div>
                  <Badge variant={status.variant}>{status.label}</Badge>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PenaltiesTab({ employeeId }: { employeeId: number }) {
  const { data, isLoading } = usePenalties({ employee_id: employeeId, status: 'all', per_page: 10 });
  const rows = unwrapRows(data);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Штрафы
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSpinner />
        ) : rows.length === 0 ? (
          <p className="text-gray-500">Штрафы не найдены.</p>
        ) : (
          <div className="space-y-3">
            {rows.map((row: any) => {
              const status = penaltyStatus[row.status] || { label: row.status, variant: 'secondary' as const };
              return (
                <div key={row.id} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-medium">{formatDate(row.penalty_date)} · {penaltyType[row.penalty_type] || row.penalty_type}</p>
                      <p className="text-sm text-gray-600">{row.reason || 'Причина не указана'}</p>
                      {row.override_reason && (
                        <p className="text-xs text-slate-500">Причина изменения: {row.override_reason}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{Number(row.amount || 0).toLocaleString('ru-RU')} сум</span>
                      <Badge variant={status.variant}>{status.label}</Badge>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DeviceBindingsTab({ employee }: { employee: any }) {
  const bindings = employee.device_bindings || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Привязки к устройствам</CardTitle>
      </CardHeader>
      <CardContent>
        {bindings.length === 0 ? (
          <p className="text-gray-500">Нет привязок к устройствам</p>
        ) : (
          <div className="space-y-3">
            {bindings.map((binding: any) => (
              <div
                key={binding.id}
                className="flex flex-col gap-3 rounded-xl bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="font-medium">{binding.device?.device_name || binding.device?.device_sn || 'Терминал'}</p>
                  <p className="text-sm text-gray-500">
                    ID пользователя на устройстве: {binding.device_user_id || 'Не задан'}
                  </p>
                </div>
                <Badge variant={binding.is_active ? 'success' : 'warning'}>
                  {binding.is_active ? 'Активна' : 'Неактивна'}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


